import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

const SCALE = 2.6;

export const Lightbox = ({ pics, index, title, onClose, onIndex }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const open = index !== null;
  const n = pics.length;
  const go = (d) => { setZoom(false); onIndex((index + d + n) % n); };

  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => {
      if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); }
      if (e.key === "ArrowRight" && n > 1) go(1);
      if (e.key === "ArrowLeft" && n > 1) go(-1);
    };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  });
  useEffect(() => { setZoom(false); }, [index]);

  const aim = (x, y, el) => {
    const r = el.getBoundingClientRect();
    setOrigin(`${Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100))}% ${Math.min(100, Math.max(0, ((y - r.top) / r.height) * 100))}%`);
  };
  const toggle = (e) => { aim(e.clientX, e.clientY, e.currentTarget); setZoom((z) => !z); };
  const btn = "w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center backdrop-blur transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] bg-[#0A1016] flex flex-col" data-testid="lightbox" data-lenis-prevent
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
          <div className="flex items-center justify-between gap-4 px-4 sm:px-8 py-4 text-white">
            <p className="f-display text-xl sm:text-2xl truncate">{title}{n > 1 && <span className="text-slate-300 text-sm ml-3 f-body">{index + 1} / {n}</span>}</p>
            <div className="flex gap-2 shrink-0">
              <button className={btn} onClick={() => setZoom((z) => !z)} aria-label={zoom ? "Dézoomer" : "Zoomer"} data-testid="lightbox-zoom-button">
                {zoom ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>
              <button className={btn} onClick={onClose} aria-label="Fermer" data-testid="lightbox-close-button"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="relative flex-1 min-h-0 overflow-hidden pb-4">
            <AnimatePresence initial={false}>
              <motion.div key={pics[index]} className="absolute inset-0 flex items-center justify-center px-2 sm:px-20"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <img src={pics[index]} alt={`${title}, photo ${index + 1}`} draggable={false}
                onClick={toggle}
                onMouseMove={(e) => zoom && aim(e.clientX, e.clientY, e.currentTarget)}
                onTouchMove={(e) => { if (zoom) { const t = e.touches[0]; aim(t.clientX, t.clientY, e.currentTarget); } }}
                style={{ transformOrigin: origin, transform: `scale(${zoom ? SCALE : 1})` }}
                className={`max-w-full max-h-full object-contain select-none transition-transform duration-500 ease-out ${zoom ? "cursor-zoom-out touch-none" : "cursor-zoom-in"}`}
                data-testid="lightbox-image" data-zoomed={zoom} />
              </motion.div>
            </AnimatePresence>
            {n > 1 && (
              <>
                <button className={`${btn} absolute left-3 sm:left-6 top-1/2 -translate-y-1/2`} onClick={() => go(-1)} aria-label="Photo précédente" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
                <button className={`${btn} absolute right-3 sm:right-6 top-1/2 -translate-y-1/2`} onClick={() => go(1)} aria-label="Photo suivante" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
              </>
            )}
          </div>
          <p className="text-center text-slate-300 text-xs sm:text-sm pb-5 px-4">{zoom ? "Déplacez la souris ou le doigt pour explorer les détails" : "Touchez l'image pour zoomer sur les papiers et les filets d'or"}</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
